<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { cloudApi } from '@/api/index.js'
import { authStore } from '@/stores/auth.js'

const route = useRoute()
const loading = ref(false)
const errorMessage = ref('')
const items = ref([])
const search = ref('')

const tenantId = computed(() => String(authStore.state.selectedTenant?.id || ''))
const projectId = computed(() => String(route.params.projectId || authStore.state.assignment?.project?.id || ''))
const visibleItems = computed(() => {
  const needle = search.value.trim().toLowerCase()
  if (!needle) return items.value
  return items.value.filter((item) => [item.display_name, item.masked_phone, item.app_code]
    .some((value) => String(value || '').toLowerCase().includes(needle)))
})
const pendingTasks = computed(() => items.value.reduce((sum, item) => sum + Number(item.metrics?.due_task_count || 0), 0))
const pendingUrls = computed(() => items.value.reduce((sum, item) => sum + Math.max(0, Number(item.metrics?.submitted_url_count || 0) - Number(item.metrics?.verified_url_count || 0)), 0))

async function loadApps() {
  if (!tenantId.value || !projectId.value) return
  loading.value = true
  errorMessage.value = ''
  try {
    const query = new URLSearchParams({
      tenant_id: tenantId.value,
      project_id: projectId.value,
      period: 'month',
      page: '1',
      page_size: '100',
    })
    const payload = await cloudApi.get(`/api/project-manager/apps?${query}`)
    items.value = Array.isArray(payload?.items) ? payload.items : []
  } catch (error) {
    errorMessage.value = error?.message || '暂时无法读取用户 App，请稍后重试。'
  } finally {
    loading.value = false
  }
}

onMounted(loadApps)
</script>

<template>
  <section class="user-apps-page" aria-labelledby="user-apps-title">
    <header class="page-header">
      <div><p class="eyebrow">AI PROJECT MANAGER</p><h1 id="user-apps-title">我的用户 App</h1><p>只显示当前项目中分配给你的正式 App；历史测试安装不会进入这里。</p></div>
      <button type="button" :disabled="loading" @click="loadApps">{{ loading ? '正在同步…' : '重新读取' }}</button>
    </header>

    <section class="assistant-card" aria-label="AI项目经理提醒">
      <div class="assistant-mark">AI</div>
      <div><strong>AI 项目经理提醒</strong><p v-if="items.length">你负责 {{ items.length }} 个正式 App，当前有 {{ pendingTasks }} 条到期任务、{{ pendingUrls }} 条 URL 等待验证。</p><p v-else>当前还没有正式用户。请先让用户在新版 AI 营销助手登记姓名和手机号，再扫描人设二维码加入项目。</p></div>
      <RouterLink :to="`/project-manager/projects/${projectId}/personas`">生成绑定二维码</RouterLink>
    </section>

    <p v-if="errorMessage" class="error-message" role="alert">{{ errorMessage }}</p>
    <label class="search-field">查找用户<input v-model.trim="search" type="search" placeholder="姓名、脱敏手机号或 App 唯一码"></label>

    <div v-if="visibleItems.length" class="app-grid">
      <article v-for="item in visibleItems" :key="item.assignment_id" class="app-card">
        <header><div><strong>{{ item.display_name }}</strong><span>{{ item.masked_phone }}</span></div><b>{{ item.app_code }}</b></header>
        <dl><div><dt>项目</dt><dd>{{ item.project_name }}</dd></div><div><dt>最近在线</dt><dd>{{ item.last_seen_at ? new Date(item.last_seen_at).toLocaleString('zh-CN') : '尚未上线' }}</dd></div><div><dt>任务</dt><dd>{{ item.metrics?.task_count ?? '待汇总' }}</dd></div><div><dt>已验证 URL</dt><dd>{{ item.metrics?.verified_url_count ?? '待汇总' }}</dd></div></dl>
        <p v-if="item.metrics?.data_status?.code === 'pending'" class="pending-state">源数据正在汇总，不能把待同步误显示为 0。</p>
        <RouterLink :to="`/project-manager/projects/${projectId}/daily-tasks`">查看和派发任务</RouterLink>
      </article>
    </div>
    <p v-else class="empty-state">{{ loading ? 'AI 项目经理正在读取正式用户…' : search ? '没有符合条件的正式用户。' : '当前项目尚无正式用户 App。' }}</p>
  </section>
</template>

<style scoped>
.user-apps-page { display: grid; gap: 22px; color: #17313b; }
.page-header { display: flex; justify-content: space-between; gap: 24px; align-items: start; padding: 26px; border: 1px solid #dce7ea; border-radius: 18px; background: #fff; }
.page-header h1, .page-header p, .assistant-card p { margin: 0; }.page-header h1 { margin: 5px 0 8px; font-size: 30px; }.page-header p { color: #6b7f87; }
.eyebrow { color: #08746d !important; font-size: 11px; font-weight: 900; letter-spacing: .14em; }.page-header button { padding: 10px 16px; border: 0; border-radius: 10px; color: #fff; background: #08746d; font-weight: 800; cursor: pointer; }
.assistant-card { display: grid; grid-template-columns: 48px minmax(0,1fr) auto; gap: 16px; align-items: center; padding: 20px; border: 1px solid #bfe4df; border-radius: 16px; background: #eaf8f5; }.assistant-mark { display: grid; place-items: center; width: 48px; height: 48px; border-radius: 14px; color: #fff; background: #08746d; font-weight: 900; }.assistant-card p { margin-top: 5px; color: #506b73; }.assistant-card a, .app-card a { color: #08746d; font-weight: 800; }
.search-field { display: grid; gap: 7px; max-width: 520px; font-weight: 800; }.search-field input { min-height: 46px; padding: 0 14px; border: 1px solid #cad9dd; border-radius: 11px; font: inherit; }.app-grid { display: grid; grid-template-columns: repeat(auto-fit,minmax(290px,1fr)); gap: 16px; }.app-card { display: grid; gap: 16px; padding: 20px; border: 1px solid #dce7ea; border-radius: 16px; background: #fff; }.app-card header { display: flex; justify-content: space-between; gap: 14px; }.app-card header strong,.app-card header span { display: block; }.app-card header span { margin-top: 4px; color: #6b7f87; }.app-card header b { color: #08746d; font-size: 13px; }.app-card dl { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 0; }.app-card dt { color: #7b8e95; font-size: 11px; }.app-card dd { margin: 4px 0 0; font-weight: 800; }.pending-state,.error-message { margin: 0; padding: 10px 12px; border-radius: 10px; }.pending-state { color: #875a00; background: #fff7d6; }.error-message { color: #9b2937; background: #fff0f1; }.empty-state { padding: 40px; text-align: center; color: #6b7f87; border: 1px dashed #c8d8dc; border-radius: 16px; background: #fff; }
@media (max-width: 700px) { .page-header,.assistant-card { grid-template-columns: 1fr; display: grid; }.page-header button { width: 100%; }.app-card dl { grid-template-columns: 1fr; } }
</style>
